/**
 * Pure helper for deriving standardized metadata from app store object.
 * Keeps view/billing/install state logic in one place so components & future
 * Vue 3 composables can share.
 */

/**
 * @typedef {Object} derived_app_meta
 * @property {boolean} price_is_set
 * @property {boolean} is_free
 * @property {boolean} is_installed
 * @property {boolean} is_update_eligible
 * @property {boolean} has_free_trial
 * @property {number|null} monthly_fee_millicents
 * @property {number|undefined} installed_version
 * @property {number|undefined} version
 * @property {number|undefined} free_trial_days_remaining
 */

/**
 * Derive normalized flags & values from a raw app object.
 * @param {Object|null|undefined} app
 * @returns {derived_app_meta}
 */
export function deriveAppMeta(app) {
  if (!app) {
    return {
      price_is_set: false,
      is_free: false,
      is_installed: false,
      is_update_eligible: false,
      has_free_trial: false,
      monthly_fee_millicents: null,
      installed_version: undefined,
      version: undefined,
      free_trial_days_remaining: undefined,
    };
  }

  const installed_version = app.installed_version;
  const version = app.version;
  const price_is_set = app.monthly_fee_millicents != null;
  const is_free = price_is_set && app.monthly_fee_millicents === 0;
  const is_installed = !!(installed_version > 0);
  const is_update_eligible = is_installed && installed_version < version;
  const free_trial_days_remaining = app.free_trial_days_remaining;
  const has_free_trial = !!(free_trial_days_remaining && free_trial_days_remaining > 0);

  return {
    price_is_set,
    is_free,
    is_installed,
    is_update_eligible,
    has_free_trial,
    monthly_fee_millicents: app.monthly_fee_millicents ?? null,
    installed_version,
    version,
    free_trial_days_remaining,
  };
}
